import { useMemo, useState } from 'react'
import { Alert, Spinner } from 'react-bootstrap'
import { useMutation, useQuery, useLazyQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { GET_MESSAGE_TEMPLATES } from '../../gql/queries/campaigns'
import { GET_ALL_ORGANIZATIONS } from '../../gql/queries/organizationsQuery'
import {
  CREATE_MESSAGE_TEMPLATE,
  UPDATE_MESSAGE_TEMPLATE,
  DUPLICATE_MESSAGE_TEMPLATE,
  TOGGLE_TEMPLATE_BLUEPRINT,
  DELETE_MESSAGE_TEMPLATE,
  RESTORE_MESSAGE_TEMPLATE,
} from '../../gql/mutations/campaigns'
import { ConfirmationModal } from '../../components/ConfirmationModal'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { TEMPLATE_FILTER_KEYS } from '../../constants/campaigns'
import { IMessageTemplate, IMessageTemplatesRes, CampaignChannel, ChannelVariantInput } from '../../types/campaigns'
import { TemplatesPanel } from '../campaigns/components/TemplatesPanel'
import { FormattedMessage } from 'react-intl'
import { TEMPLATE_MESSAGES as M } from './templateMessages'
import { TemplateFormModal } from '../campaigns/components/TemplateFormModal'
import { TemplateStarterModal } from './components/TemplateStarterModal'
import { TemplateStarter } from '../../constants/templateStarters'
import { useGetUser } from '../../store/userStore'
import { isAdministrator, parseId } from '../../helpers'
import { CampaignsNav } from '../campaigns/components/CampaignsNav'

export const TemplatesPage = () => {
  const navigate = useNavigate()
  const permissions = useGetUser((state) => state.user?.permissions ?? [])
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const currentUserId = useGetUser((s) => s.user?.user?.id)
  const canManageBlueprints = isAdministrator(permissions)
  const canSeeOwner = isAdministrator(permissions) || userGroups.includes('Supervisor')
  const isSuperAdmin = userGroups.includes('SuperAdministrador')

  const [starterModalOpen, setStarterModalOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<IMessageTemplate | null>(null)
  const [defaultChannel, setDefaultChannel] = useState<CampaignChannel | undefined>(undefined)
  const [selectedStarter, setSelectedStarter] = useState<TemplateStarter | null>(null)

  // Duplicate state
  const [duplicateFormOpen, setDuplicateFormOpen] = useState(false)
  const [templateToDuplicate, setTemplateToDuplicate] = useState<IMessageTemplate | null>(null)

  // Delete / archive state
  const [templateToDelete, setTemplateToDelete] = useState<IMessageTemplate | null>(null)
  const [showArchived, setShowArchived] = useLocalStorage(
    TEMPLATE_FILTER_KEYS.showArchived,
    false
  )

  const {
    data,
    loading,
    error,
    refetch,
  } = useQuery<IMessageTemplatesRes>(GET_MESSAGE_TEMPLATES, {
    variables: { includeArchived: showArchived },
    fetchPolicy: 'cache-and-network',
  })

  const [loadOrgs, { data: orgsData }] = useLazyQuery(GET_ALL_ORGANIZATIONS, {
    fetchPolicy: 'cache-first',
  })

  const templates = useMemo(() => data?.messageTemplates ?? [], [data])

  const organizations = useMemo(() => {
    const edges = orgsData?.allOrganizations?.edges ?? []
    return edges.map((e: any) => ({ id: parseId(e.node.id), name: e.node.name }))
  }, [orgsData])

  const [createTemplateMutation, { loading: creating }] = useMutation(CREATE_MESSAGE_TEMPLATE)
  const [updateTemplateMutation, { loading: updating }] = useMutation(UPDATE_MESSAGE_TEMPLATE)
  const [duplicateTemplateMutation, { loading: duplicating }] = useMutation(DUPLICATE_MESSAGE_TEMPLATE)
  const [toggleBlueprintMutation] = useMutation(TOGGLE_TEMPLATE_BLUEPRINT)
  const [deleteTemplateMutation, { loading: deleting }] = useMutation(DELETE_MESSAGE_TEMPLATE)
  const [restoreTemplateMutation] = useMutation(RESTORE_MESSAGE_TEMPLATE)

  const handleClose = () => {
    setModalOpen(false)
    setEditingTemplate(null)
    setDefaultChannel(undefined)
    setSelectedStarter(null)
  }

  const handleOpenCreate = (channel?: CampaignChannel) => {
    setEditingTemplate(null)
    setDefaultChannel(channel)
    setStarterModalOpen(true)
  }

  const handleSelectStarter = (starter: TemplateStarter) => {
    setSelectedStarter(starter)
    setDefaultChannel(starter.channel)
    setStarterModalOpen(false)
    setModalOpen(true)
  }

  const handleStartFromScratch = () => {
    setSelectedStarter(null)
    setStarterModalOpen(false)
    setModalOpen(true)
  }

  const handleEditTemplate = (template: IMessageTemplate) => {
    if (template.isBlueprint && !canManageBlueprints) {
      toast.info('Only administrators can edit blueprint templates. You can duplicate it to create your own version.')
      return
    }
    setEditingTemplate(template)
    setDefaultChannel(template.channel)
    setModalOpen(true)
  }

  const handleSubmit = async (payload: {
    name: string
    description?: string
    channel: CampaignChannel
    subject?: string
    body: string
    variables?: string[]
    metadata?: Record<string, any>
    enabledChannels?: CampaignChannel[]
    organizationId?: string
    variants?: ChannelVariantInput[]
  }) => {
    try {
      if (editingTemplate) {
        const { variants, ...updatePayload } = payload
        await updateTemplateMutation({
          variables: {
            id: editingTemplate.id,
            ...updatePayload,
          },
        })
        toast.success('Template updated')
        handleClose()
        await refetch()
        return
      }

      const { data: created } = await createTemplateMutation({
        variables: payload,
      })
      toast.success('Template created')
      handleClose()
      await refetch()

      // Jump straight into the variant editor: the starter content is already there.
      const newTemplateId = created?.createMessageTemplate?.template?.id
      if (newTemplateId) {
        navigate(`/templates/edit/${newTemplateId}`)
      }
    } catch (error) {
      toast.error('Unable to save template')
    }
  }

  const handleDuplicateClick = (template: IMessageTemplate) => {
    setTemplateToDuplicate(template)
    if (isSuperAdmin) loadOrgs()
    setDuplicateFormOpen(true)
  }

  const handleDuplicateFormSubmit = async (payload: {
    name: string
    organizationId?: string
    [key: string]: any
  }) => {
    if (!templateToDuplicate) return
    try {
      await duplicateTemplateMutation({
        variables: {
          id: templateToDuplicate.id,
          newName: payload.name,
          organizationId: payload.organizationId || undefined,
        },
      })
      toast.success('Template duplicated')
      await refetch()
      setDuplicateFormOpen(false)
      setTemplateToDuplicate(null)
    } catch (e) {
      toast.error('Could not duplicate template')
    }
  }

  const handleDeleteClick = (template: IMessageTemplate) => {
    if (template.isBlueprint) {
      toast.info('Blueprints cannot be deleted. Remove the blueprint mark (⭐) first.')
      return
    }
    setTemplateToDelete(template)
  }

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return
    try {
      const { data: result } = await deleteTemplateMutation({
        variables: { id: templateToDelete.id },
      })
      const payload = result?.deleteMessageTemplate
      if (payload?.archived) {
        toast.info('Template archived because it is still in use')
      } else {
        toast.success('Template deleted')
      }
      setTemplateToDelete(null)
      await refetch()
    } catch (e) {
      toast.error('Could not delete template')
    }
  }

  const handleRestore = async (template: IMessageTemplate) => {
    try {
      await restoreTemplateMutation({ variables: { id: template.id } })
      toast.success('Template restored')
      await refetch()
    } catch (e) {
      toast.error('Could not restore template')
    }
  }

  const handleToggleBlueprint = async (template: IMessageTemplate, isBlueprint: boolean) => {
    if (!canManageBlueprints) {
      toast.error('Only administrators can change blueprint status')
      return
    }
    try {
      await toggleBlueprintMutation({ variables: { id: template.id, isBlueprint } })
      toast.success(isBlueprint ? '⭐ Template marked as blueprint' : 'Blueprint status removed')
      await refetch()
    } catch (e) {
      toast.error('Could not update blueprint status')
    }
  }

  return (
    <div className='templates-page'>
      <CampaignsNav />
      <div className='d-flex align-items-center justify-content-between mb-6 flex-wrap gap-3'>
        <div>
          <h1 className='page-title'><FormattedMessage {...M.pageTitle} /></h1>
          <div className='text-muted'>
            <FormattedMessage {...M.pageSubtitle} />
          </div>
        </div>
        {loading && <Spinner animation='border' size='sm' />}
      </div>
      {error && (
        <Alert variant='danger'>
          <FormattedMessage {...M.pageLoadError} values={{message: error.message}} />
        </Alert>
      )}
      <TemplatesPanel
        templates={templates}
        onCreate={() => handleOpenCreate()}
        onEdit={handleEditTemplate}
        onDuplicate={handleDuplicateClick}
        onDelete={handleDeleteClick}
        onRestore={handleRestore}
        showArchived={showArchived}
        onToggleShowArchived={setShowArchived}
        onToggleBlueprint={handleToggleBlueprint}
        canManageBlueprints={canManageBlueprints}
        canSeeOwner={canSeeOwner}
        isSuperAdmin={isSuperAdmin}
        isAdmin={canManageBlueprints}
        currentUserId={currentUserId}
      />
      <TemplateStarterModal
        show={starterModalOpen}
        onHide={() => setStarterModalOpen(false)}
        onSelect={handleSelectStarter}
        onStartFromScratch={handleStartFromScratch}
      />
      <TemplateFormModal
        show={modalOpen}
        onHide={handleClose}
        onSubmit={handleSubmit}
        template={editingTemplate}
        starter={selectedStarter}
        loading={creating || updating}
        defaultChannel={defaultChannel}
      />

      {templateToDelete && (() => {
        const usage = templateToDelete.usage
        const inUse = (usage?.total ?? 0) > 0
        const variantCount = templateToDelete.variants?.length ?? 0
        return (
          <ConfirmationModal
            show
            onHide={() => setTemplateToDelete(null)}
            onConfirm={handleConfirmDelete}
            loading={deleting}
            variant={inUse ? 'warning' : 'danger'}
            title={inUse ? 'Archive template' : 'Delete template'}
            confirmText={inUse ? 'Archive' : 'Delete'}
            message={
              inUse ? (
                <>
                  <p>
                    <strong>{templateToDelete.name}</strong> is still being used, so it will be
                    archived instead of deleted. Deleting it would leave those items without content.
                  </p>
                  <ul className='mb-3'>
                    {(usage?.campaigns ?? 0) > 0 && <li>{usage?.campaigns} campaign(s)</li>}
                    {(usage?.automations ?? 0) > 0 && <li>{usage?.automations} automation(s)</li>}
                    {(usage?.automationTemplates ?? 0) > 0 && (
                      <li>{usage?.automationTemplates} automation template(s)</li>
                    )}
                  </ul>
                  <p className='text-muted mb-0'>
                    It will disappear from the list but keeps working where it is already in use.
                    You can bring it back with “Show archived”.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    Delete <strong>{templateToDelete.name}</strong> permanently? Nothing is using it,
                    so this is safe — but it cannot be undone.
                  </p>
                  {variantCount > 0 && (
                    <p className='text-muted mb-0'>
                      Its {variantCount} channel variant{variantCount !== 1 ? 's' : ''} will be
                      deleted too.
                    </p>
                  )}
                </>
              )
            }
          />
        )
      })()}

      {/* Duplicate — siempre modal completo; org selector solo visible para superadmin */}
      {templateToDuplicate && (
        <TemplateFormModal
          show={duplicateFormOpen}
          onHide={() => { setDuplicateFormOpen(false); setTemplateToDuplicate(null) }}
          onSubmit={handleDuplicateFormSubmit}
          template={{ ...templateToDuplicate, name: `${templateToDuplicate.name} (Copy)` }}
          loading={duplicating}
          isDuplicate
          organizations={isSuperAdmin ? organizations : undefined}
          defaultOrganizationId={isSuperAdmin ? templateToDuplicate.organization?.id : undefined}
        />
      )}
    </div>
  )
}
